import { useLingui } from '@lingui/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ZeitkontoProjection } from '@/types/zeitkonto';

interface EmployeeZeitkontoBadgeProps {
  projection?: ZeitkontoProjection;
  className?: string;
}

/**
 * Format a decimal-hours value as a signed `Xh Ym` string.
 * e.g. 1.5 -> "+1h 30m", -2.25 -> "-2h 15m", 0 -> "+0h 0m"
 */
function formatSignedHours(hours: number): string {
  const sign = hours >= 0 ? '+' : '-';
  const absHours = Math.abs(hours);
  const wholeHours = Math.floor(absHours);
  // Round minutes to avoid floating-point drift (e.g. 0.1*60 = 5.999...)
  const minutes = Math.round((absHours - wholeHours) * 60);
  // Rounding can push minutes to 60 — carry into hours
  if (minutes === 60) {
    return `${sign}${wholeHours + 1}h 0m`;
  }
  return `${sign}${wholeHours}h ${minutes}m`;
}

/**
 * Color class mirroring DeltaIndicator semantics, applied to the projected
 * balance magnitude (expressed in hours):
 * - green: |projected| < 1h (on target)
 * - yellow: 1h <= |projected| < 4h (minor deviation)
 * - red: |projected| >= 4h (major deviation)
 */
function getColorClass(projectedHours: number): string {
  const abs = Math.abs(projectedHours);
  if (abs < 1) return 'text-success-on-surface';
  if (abs < 4) return 'text-warning-on-surface';
  return 'text-destructive-on-surface';
}

/**
 * Compact inline badge showing an employee's Zeitkonto balance:
 * current carry-over → projected (current + planned - target) for the window.
 *
 * Accessibility: the signed numeric values are always rendered (never
 * color-only), and an aria-label + tooltip expose the full
 * current/planned/target/projected breakdown.
 *
 * Renders nothing when no projection is available.
 */
export function EmployeeZeitkontoBadge({
  projection,
  className,
}: EmployeeZeitkontoBadgeProps) {
  const { i18n } = useLingui();

  // Gracefully handle a missing/undefined projection: render nothing.
  if (!projection) {
    return null;
  }

  const currentFormatted = formatSignedHours(projection.current_hours);
  const plannedFormatted = formatSignedHours(projection.planned_hours);
  const targetFormatted = formatSignedHours(projection.target_hours);
  const projectedFormatted = formatSignedHours(projection.projected_hours);
  const colorClass = getColorClass(projection.projected_hours);

  const ariaLabel = i18n._({
    id: 'zeitkonto.badge.ariaLabel',
    message:
      'Working time account: current {current}, planned {planned}, target {target}, projected {projected}',
    values: {
      current: currentFormatted,
      planned: plannedFormatted,
      target: targetFormatted,
      projected: projectedFormatted,
    },
  });

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center gap-0.5 text-xs font-medium whitespace-nowrap cursor-pointer',
              className
            )}
            aria-label={ariaLabel}
          >
            <span className="text-muted-foreground">{currentFormatted}</span>
            <span aria-hidden="true" className="text-muted-foreground">
              →
            </span>
            <span className={colorClass}>{projectedFormatted}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm space-y-1">
            <div className="font-semibold">
              {i18n._({
                id: 'zeitkonto.badge.tooltip.title',
                message: 'Working time account',
              })}
            </div>
            <div>
              {i18n._({
                id: 'zeitkonto.badge.tooltip.current',
                message: 'Current: {value}',
                values: { value: currentFormatted },
              })}
            </div>
            <div>
              {i18n._({
                id: 'zeitkonto.badge.tooltip.planned',
                message: 'Planned: {value}',
                values: { value: plannedFormatted },
              })}
            </div>
            <div>
              {i18n._({
                id: 'zeitkonto.badge.tooltip.target',
                message: 'Target: {value}',
                values: { value: targetFormatted },
              })}
            </div>
            <div className="font-medium">
              {i18n._({
                id: 'zeitkonto.badge.tooltip.projected',
                message: 'Projected: {value}',
                values: { value: projectedFormatted },
              })}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
